import React, { useEffect, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { ReportGenUser } from "../../../shared/interfaces/User";
import {
  assignableStationRoles,
  getStationRoleLabel,
} from "../../../shared/constants/roleConstants";
import { ConfirmDialog } from "../../../shared/ui";

export interface EditRoleDialogProps {
  open: boolean;
  user: ReportGenUser | null;
  /** The caller's own role slug — decides which roles the picker offers. */
  callerRoleSlug: string | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (roleSlug: string) => void;
}

const EditRoleDialog: React.FC<EditRoleDialogProps> = ({
  open,
  user,
  callerRoleSlug,
  isSaving,
  onClose,
  onSave,
}) => {
  const [roleSlug, setRoleSlug] = useState("");

  const options = assignableStationRoles(callerRoleSlug);

  // Re-seed from the row each time the dialog opens for a user. The current
  // role may not be assignable by this caller (e.g. it defaulted from an
  // unranked slug) — only preselect it when it's actually offered.
  useEffect(() => {
    if (open) {
      const current = user?.role ?? "";
      setRoleSlug(options.some((role) => role.slug === current) ? current : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const isChanged = roleSlug !== "" && roleSlug !== (user?.role ?? "");
  const selected = options.find((role) => role.slug === roleSlug);

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={() => {
        if (isChanged && !isSaving) {
          onSave(roleSlug);
        }
      }}
      icon={<ManageAccountsOutlinedIcon />}
      iconVariant="tinted"
      title="Change Role"
      description={
        user
          ? `Update the role for ${[user.firstName, user.lastName].filter(Boolean).join(" ") || user.email}. Current role: ${getStationRoleLabel(user.role) ?? "None"}.`
          : undefined
      }
      confirmLabel="Save"
      loading={isSaving}
      confirmDisabled={!isChanged}
    >
      <TextField
        select
        fullWidth
        label="Role"
        value={roleSlug}
        onChange={(e) => setRoleSlug(e.target.value)}
        helperText={selected?.description ?? " "}
        sx={{ mt: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      >
        {options.map((role) => (
          <MenuItem key={role.slug} value={role.slug}>
            {role.label}
          </MenuItem>
        ))}
      </TextField>
    </ConfirmDialog>
  );
};

export default EditRoleDialog;
